<template>
	<Transition name="slide-down">
		<div v-if="updateAvailable" class="sw-update-banner">
			<div class="sw-update-content">
				<div class="sw-update-icon">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
						stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<polyline points="23 4 23 10 17 10"></polyline>
						<polyline points="1 20 1 14 7 14"></polyline>
						<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
					</svg>
				</div>
				<div class="sw-update-text">
					<h6 class="mb-0">New Version Available!</h6>
					<p class="mb-0 small text-muted">We've made improvements to the app.</p>
				</div>
				<div class="sw-update-actions">
					<button @click="dismissUpdate" class="btn btn-sm btn-light me-2" type="button">
						Later
					</button>
					<button @click="applyUpdate" class="btn btn-sm btn-primary" type="button" :disabled="updating">
						<span v-if="updating" class="spinner-border spinner-border-sm me-1"></span>
						{{ updating ? 'Updating...' : 'Update Now' }}
					</button>
				</div>
			</div>
		</div>
	</Transition>
</template>

<script>
export default {
	name: 'ServiceWorkerUpdate',
	data() {
		return {
			updateAvailable: false,
			updating: false,
			registration: null,
			newWorker: null
		};
	},
	mounted() {
		this.registerServiceWorker();
		this.listenForUpdates();
	},
	methods: {
		async registerServiceWorker() {
			if (!('serviceWorker' in navigator)) {
				console.log('[SW Update] Service workers not supported');
				return;
			}

			try {
				// Register the service worker
				const registration = await navigator.serviceWorker.register('/sw.js', {
					updateViaCache: 'none' // Always check for updates
				});

				this.registration = registration;
				console.log('[SW Update] Service Worker registered successfully');

				// Check for updates on page load
				registration.update();

				// Check for updates periodically (every 60 seconds)
				setInterval(() => {
					console.log('[SW Update] Checking for updates...');
					registration.update();
				}, 60000);

				// Listen for new service worker installing
				registration.addEventListener('updatefound', () => {
					console.log('[SW Update] New service worker found!');
					this.newWorker = registration.installing;

					this.newWorker.addEventListener('statechange', () => {
						console.log('[SW Update] New worker state:', this.newWorker.state);

						// Show update banner when new worker is installed and waiting
						if (this.newWorker.state === 'installed' && navigator.serviceWorker.controller) {
							console.log('[SW Update] New version is waiting to activate');
							this.showUpdateBanner();
						}
					});
				});

				// Handle controller change (new SW activated)
				navigator.serviceWorker.addEventListener('controllerchange', () => {
					console.log('[SW Update] Controller changed, reloading page...');
					if (!this.updating) {
						window.location.reload();
					}
				});

			} catch (error) {
				console.error('[SW Update] Service Worker registration failed:', error);
			}
		},

		listenForUpdates() {
			// Listen for messages from service worker
			navigator.serviceWorker.addEventListener('message', (event) => {
				console.log('[SW Update] Message from SW:', event.data);

				if (event.data && event.data.type === 'SW_UPDATED') {
					console.log('[SW Update] Service worker updated to version:', event.data.version);
					this.showUpdateBanner();
				}
			});
		},

		showUpdateBanner() {
			// Don't show if user dismissed recently
			const dismissed = sessionStorage.getItem('sw_update_dismissed');
			if (dismissed) {
				console.log('[SW Update] Update banner was dismissed this session');
				return;
			}

			this.updateAvailable = true;
			console.log('[SW Update] Showing update banner');
		},

		dismissUpdate() {
			this.updateAvailable = false;
			sessionStorage.setItem('sw_update_dismissed', 'true');
			console.log('[SW Update] Update dismissed for this session');
		},

		async applyUpdate() {
			if (!this.newWorker) {
				console.log('[SW Update] No new worker available, reloading anyway...');
				window.location.reload();
				return;
			}

			this.updating = true;
			console.log('[SW Update] Applying update...');

			try {
				// Tell the new service worker to skip waiting
				this.newWorker.postMessage({ type: 'SKIP_WAITING' });

				// The page will reload automatically when controllerchange fires
				setTimeout(() => {
					// Fallback reload if controllerchange doesn't fire
					console.log('[SW Update] Forcing reload...');
					window.location.reload();
				}, 2000);

			} catch (error) {
				console.error('[SW Update] Error applying update:', error);
				this.updating = false;
				window.location.reload();
			}
		}
	}
};
</script>

<style scoped>
.sw-update-banner {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 9999;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	color: white;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	animation: slideDown 0.3s ease-out;
}

.sw-update-content {
	max-width: 1200px;
	margin: 0 auto;
	padding: 12px 20px;
	display: flex;
	align-items: center;
	gap: 16px;
}

.sw-update-icon {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 50%;
	animation: pulse 2s infinite;
}

.sw-update-icon svg {
	animation: rotate 2s linear infinite;
}

.sw-update-text {
	flex: 1;
}

.sw-update-text h6 {
	font-weight: 600;
	font-size: 14px;
	margin-bottom: 2px;
}

.sw-update-text p {
	font-size: 12px;
	opacity: 0.9;
}

.sw-update-actions {
	flex-shrink: 0;
	display: flex;
	gap: 8px;
}

.sw-update-actions .btn {
	font-size: 13px;
	padding: 6px 16px;
	border-radius: 20px;
	transition: all 0.2s;
}

.sw-update-actions .btn-light {
	background: rgba(255, 255, 255, 0.9);
	border: none;
	color: #667eea;
}

.sw-update-actions .btn-light:hover {
	background: white;
	transform: translateY(-1px);
}

.sw-update-actions .btn-primary {
	background: white;
	border: none;
	color: #667eea;
	font-weight: 600;
}

.sw-update-actions .btn-primary:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Animations */
.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.3s ease-out;
}

.slide-down-enter-from {
	transform: translateY(-100%);
	opacity: 0;
}

.slide-down-leave-to {
	transform: translateY(-100%);
	opacity: 0;
}

@keyframes slideDown {
	from {
		transform: translateY(-100%);
		opacity: 0;
	}

	to {
		transform: translateY(0);
		opacity: 1;
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
		opacity: 1;
	}

	50% {
		transform: scale(1.05);
		opacity: 0.8;
	}
}

@keyframes rotate {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}

/* Responsive */
@media (max-width: 768px) {
	.sw-update-content {
		flex-wrap: wrap;
		padding: 12px 16px;
	}

	.sw-update-text {
		flex-basis: 100%;
		order: 2;
		margin-top: 8px;
	}

	.sw-update-actions {
		width: 100%;
		order: 3;
		margin-top: 8px;
	}

	.sw-update-actions .btn {
		flex: 1;
	}
}
</style>
